import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import Icon from './Icon';
import ShieldSpinner from './ShieldSpinner';
import s from './Dialog.module.css';

// Battle-branded dialogs in place of the browser's confirm / alert / prompt. Call from anywhere — no hook needed:
//   if (await confirm({ title: 'Delete “x”?', danger: true, confirmLabel: 'Delete' })) …
//   await alert({ title: 'Photo didn’t upload', message: e.message, tone: 'error' });
//   const why = await prompt({ title: 'Why?', placeholder: 'optional' });   // null = cancelled
//   const v = await choose({ title: 'Clock out', message: 'Is it done?', options: [{ label: 'No', value: 'no' }, { label: 'Yes', value: 'yes', primary: true }] });   // null = cancelled
// A string works as the whole argument (the title). <DialogHost/> in the shell draws them, one at a time, in order.
// `run` on a confirm keeps the dialog open with the shield spinner until the async work finishes (or throws —
// then the error shows in the dialog and it stays open).

let queue = [];
const subs = new Set();
const emit = () => subs.forEach((f) => f());
const open = (kind, opts) => new Promise((resolve) => {
  const o = typeof opts === 'string' ? { title: opts } : { ...opts };
  queue = [...queue, { id: Math.random().toString(36).slice(2), kind, ...o, resolve }];
  emit();
});
const close = (d, value) => { queue = queue.filter((q) => q !== d); emit(); d.resolve(value); };

export const confirm = (opts) => open('confirm', opts);
export const alert = (opts) => open('alert', opts);
export const prompt = (opts) => open('prompt', opts);
export const choose = (opts) => open('choose', opts);

export function DialogHost() {
  const list = useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => queue);
  const d = list[0];
  return d ? <Dialog key={d.id} d={d} /> : null;
}

const TONE_ICON = { danger: 'alert-triangle', error: 'x-circle', info: 'info', done: 'check-circle', ask: 'help-circle', edit: 'edit-3' };

function Dialog({ d }) {
  const [value, setValue] = useState(d.value ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const input = useRef(null);
  const ok = useRef(null);
  const tone = d.tone || (d.danger ? 'danger' : d.kind === 'prompt' ? 'edit' : d.kind === 'alert' ? 'info' : 'ask');
  const cancelValue = d.kind === 'confirm' ? false : d.kind === 'prompt' || d.kind === 'choose' ? null : undefined;

  const cancel = () => { if (!busy) close(d, cancelValue); };
  const accept = async () => {
    if (busy) return;
    if (d.kind === 'prompt') { if (d.required && !value.trim()) { input.current?.focus(); return; } close(d, value); return; }
    if (d.kind === 'alert') { close(d); return; }
    if (d.kind === 'choose') { const p = (d.options || []).find((o) => o.primary) || (d.options || [])[0]; close(d, p ? p.value : null); return; }
    if (d.run) {
      setBusy(true); setErr(null);
      try { await d.run(); close(d, true); } catch (e) { setErr(e?.message || String(e)); setBusy(false); }
      return;
    }
    close(d, true);
  };

  useEffect(() => {
    (d.kind === 'prompt' ? input.current : ok.current)?.focus();
    if (d.kind === 'prompt') input.current?.select?.();
    const key = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel(); } };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmLabel = d.confirmLabel || (d.kind === 'alert' ? 'OK' : d.kind === 'prompt' ? 'Save' : d.danger ? 'Delete' : 'Confirm');
  const Field = d.multiline ? 'textarea' : 'input';
  return (
    <div className={s.scrim} onMouseDown={(e) => { if (e.target === e.currentTarget && d.kind !== 'alert') cancel(); }}>
      <form className={s.box} data-tone={tone} role={d.kind === 'alert' ? 'alertdialog' : 'dialog'} aria-modal="true"
        aria-labelledby={`dlg-${d.id}`} onSubmit={(e) => { e.preventDefault(); accept(); }}>
        <div className={s.glow} aria-hidden />
        <div className={s.head}>
          <span className={s.badge}><Icon name={d.icon || TONE_ICON[tone] || 'help-circle'} size={18} /></span>
          <div className={s.titles}>
            {d.eyebrow ? <div className={s.eyebrow}>{d.eyebrow}</div> : null}
            <h2 id={`dlg-${d.id}`} className={s.title}>{d.title}</h2>
          </div>
        </div>
        {d.message ? <div className={s.message}>{d.message}</div> : null}
        {d.kind === 'prompt' ? (
          <label className={s.field}>
            {d.label ? <span>{d.label}</span> : null}
            <Field ref={input} className={s.input} data-multi={d.multiline || undefined} value={value} placeholder={d.placeholder || ''}
              type={d.type || 'text'} rows={d.multiline ? 4 : undefined}
              onKeyDown={(e) => { if (d.multiline && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); accept(); } }}
              onChange={(e) => setValue(e.target.value)} />
          </label>
        ) : null}
        {err ? <div className={s.err}><Icon name="alert-triangle" size={14} />{err}</div> : null}
        {d.kind === 'choose' ? (
          <div className={s.foot}>
            <button type="button" className={s.cancel} onClick={cancel}>{d.cancelLabel || 'Cancel'}</button>
            {(d.options || []).map((o) => (
              <button key={String(o.value)} ref={o.primary ? ok : undefined} type={o.primary ? 'submit' : 'button'} className={o.primary ? s.ok : s.cancel}
                onClick={o.primary ? undefined : () => close(d, o.value)}>{o.label}</button>
            ))}
          </div>
        ) : (
          <div className={s.foot}>
            {d.kind !== 'alert' ? <button type="button" className={s.cancel} onClick={cancel} disabled={busy}>{d.cancelLabel || 'Cancel'}</button> : null}
            <button ref={ok} type="submit" className={s.ok} data-danger={d.danger || undefined} disabled={busy}>
              {busy ? <><ShieldSpinner size={16} tone="currentColor" />{d.busyLabel || 'Working…'}</> : confirmLabel}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
